// Shared types + pure helpers for the cockpit's resource pools (the bottom strip
// and the Gantt rows both render the same pool). Kept out of the .tsx files so
// component modules only export components — react-refresh friendliness, same
// split as `planStyles.ts`.

import type {
  CertificationKeyValue,
  EquipmentCatalogue,
  Resource,
  ResourceCertification,
  ResourceTypeValue,
} from '@my/directory-api';

/** Predefined certification keys → short capability-badge labels. */
const CERT_LABEL: Record<CertificationKeyValue, string> = {
  Adr: 'ADR',
  Cpc: 'CPC',
  Mot: 'MOT',
  Tachograph: 'Tacho',
  DriverCard: 'Card',
  Insurance: 'Insurance',
  FirstAid: 'First Aid',
  Hiab: 'Hiab',
  Forklift: 'Forklift',
  Psv: 'PSV',
  Coshh: 'COSHH',
  OLicence: 'O-Licence',
  RoadTax: 'Road Tax',
  Custom: 'Custom',
};

/** A capability chip: a certification (blue) or a service tag (amber). */
export interface Cap {
  label: string;
  serviceTag: boolean;
}

/** Display name for a cert row — the custom label for service tags. */
const certDisplayLabel = (c: ResourceCertification): string =>
  c.certificationKey === 'Custom' ? (c.customLabel ?? 'Custom') : CERT_LABEL[c.certificationKey];

export const capOf = (c: ResourceCertification): Cap => ({
  label: certDisplayLabel(c),
  serviceTag: c.certificationKey === 'Custom',
});

// ── Compliance alerts ────────────────────────────────────────────────────────

/** `stop` blocks assignment outright; `warn` is advisory. */
export type AlertLevel = 'warn' | 'stop';

export interface ResourceAlert {
  level: AlertLevel;
  /** Human-readable reason, e.g. "Insurance expires in 12d". */
  message: string;
}

/** Whole-day difference between an ISO date and today (negative = in the past). */
function daysUntil(isoDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Turn a resource's certifications into assignment alerts.
 *
 * A cert only matters when it carries an expiry and a compliance action: an
 * expired cert whose action is `Stop` blocks the resource, an expired `Warn`
 * flags it, and anything inside its warn window is a heads-up regardless of
 * which of the two it is (it hasn't lapsed yet, so it can't be a hard stop).
 * Certs with action `None` are informational and never raise anything.
 */
export function certAlerts(certs: ResourceCertification[]): ResourceAlert[] {
  const out: ResourceAlert[] = [];
  for (const c of certs) {
    if (!c.expiryDate || c.complianceAction === 'None') continue;
    const label = certDisplayLabel(c);
    const days = daysUntil(c.expiryDate);

    if (days < 0) {
      out.push({
        level: c.complianceAction === 'Stop' ? 'stop' : 'warn',
        message: `${label} expired ${Math.abs(days)}d ago`,
      });
    } else if (c.warnDays != null && days <= c.warnDays) {
      out.push({
        level: 'warn',
        message: days === 0 ? `${label} expires today` : `${label} expires in ${days}d`,
      });
    }
  }
  // Blocking reasons first — they're what stops the planner.
  return out.sort((a, b) => (a.level === b.level ? 0 : a.level === 'stop' ? -1 : 1));
}

/** Flatten the equipment catalogue into catalogue key → display label. */
export function equipmentLabels(catalogue: EquipmentCatalogue | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const cls of catalogue?.classes ?? []) {
    for (const group of cls.groups) {
      for (const type of group.types) map.set(type.key, type.label);
    }
  }
  return map;
}

// ── Pools ────────────────────────────────────────────────────────────────────

export type PoolId =
  | 'driverRoster'
  | 'drivers'
  | 'trailers'
  | 'vehicles'
  | 'subcontractors'
  | 'subcontractorTrailers';

export const POOL_ORDER: PoolId[] = [
  'driverRoster',
  'drivers',
  'trailers',
  'vehicles',
  'subcontractors',
  'subcontractorTrailers',
];

export const POOL_LABEL: Record<PoolId, string> = {
  driverRoster: 'Driver Roster',
  drivers: 'Drivers',
  trailers: 'Trailers',
  vehicles: 'Vehicles',
  subcontractors: 'Subcontractors',
  subcontractorTrailers: 'Subcontractor Trailers',
};

/**
 * Pools that show their tab even with nothing in them.
 *
 * <p>
 * Every other pool hides when empty — an empty Trailers tab is just noise, since
 * trailers arrive by being registered elsewhere. Subcontractor trailers are the
 * exception: the only way to create one is the Add card inside this tab, so
 * hiding it when empty would make the feature unreachable from a cold start.
 * </p>
 */
export const ALWAYS_VISIBLE_POOLS: ReadonlySet<PoolId> = new Set<PoolId>(['subcontractorTrailers']);

// ── Linked vehicle + tacho ───────────────────────────────────────────────────

/** Live tacho/telematics state of a driver's linked vehicle. */
export type TachoStatus = 'Connected' | 'Driving' | 'IgnitionOff' | 'Resting' | 'Unknown';

export const TACHO_STATUS_LABEL: Record<TachoStatus, string> = {
  Connected: 'Connected',
  Driving: 'Driving',
  IgnitionOff: 'Ignition off',
  Resting: 'Resting',
  Unknown: 'Unknown',
};

export const TACHO_STATUS_ACCENT: Record<TachoStatus, string> = {
  Connected: 'text-emerald-600',
  Driving: 'text-blue-600',
  IgnitionOff: 'text-slate-400',
  Resting: 'text-amber-600',
  Unknown: 'text-slate-400',
};

/** The vehicle a driver is currently linked to, plus its tacho state. */
export interface LinkedVehicle {
  /** The vehicle resource's id — needed to assign it alongside its driver. */
  resourceId: string;
  /** Registration / fleet ref shown on the card. */
  reg: string;
  tachoStatus: TachoStatus;
}

/** One resource in a pool — rendered as a strip card and as a Gantt row. */
export interface ResourceCardVM {
  id: string;
  title: string;
  /** Line under the title (start time · location, registration, party code…). */
  subtitle: string;
  /** Small muted meta line (equipment type, contact name…). */
  meta: string | null;
  /** Resource type (Truck / Rigid / Trailer / Driver). Null for subcontractors. */
  resourceType: ResourceTypeValue | null;
  /**
   * Leading chip describing what the resource *is* — the equipment type when it
   * has one ("Standard Box Rigid"), otherwise the plain resource type ("Truck").
   * Null for drivers and subcontractors, where it says nothing useful.
   */
  typeChip: string | null;
  caps: Cap[];
  available: boolean;
  /** Shows the tacho gauge + linked-vehicle line — drivers only. */
  showTacho: boolean;
  /**
   * The driver's default vehicle, or null when none is set (the card then offers
   * "No vehicle" as a call to action). Tacho status is always `Unknown` for now
   * — knowing *which* vehicle doesn't give us telematics; that's a separate
   * integration.
   */
  linkedVehicle: LinkedVehicle | null;
  /** Amber note, e.g. "Not on the roster". */
  note: string | null;
  /** Home depot, for the depot filter. Null = unassigned (shown at the end). */
  homeSiteId: string | null;
  /**
   * The home depot's name, for display. Null = unassigned. Kept separate from
   * {@link currentLocationLabel} because a unit's base and its whereabouts are
   * different facts — collapsing them would make "where is it?" unanswerable the
   * moment telematics lands.
   */
  depotLabel: string | null;
  /**
   * Where the unit actually is right now. Always null today — it arrives with
   * the telematics feed, so the card renders a placeholder beneath the depot
   * until then (same treatment as {@link odometerKm}).
   */
  currentLocationLabel: string | null;
  /**
   * Last odometer reading in km. Always null today — it arrives with the
   * telematics feed (see the proposed VehicleTelemetryState store), so the row
   * renders a placeholder until then.
   */
  odometerKm: number | null;
  /** Compliance problems that warn about, or block, assigning this resource. */
  alerts: ResourceAlert[];
  /**
   * Drivers who have this vehicle as their default — plural, because nothing
   * stops a truck being shared (double-shifting is normal). Empty = free.
   */
  linkedDriverNames: string[];
  /**
   * How work reaches this subcontractor. Null on resources — drivers and
   * vehicles are dispatched, not sent work.
   */
  channel: SubcontractorChannel | null;
  /**
   * Subcontractor's town / city — what the card actually shows. A card is
   * 192px wide, so a full Google-formatted address would truncate to
   * uselessness; the place name is the part a planner reads. Null when the
   * party has no city on file. Resources use {@link depotLabel} instead.
   */
  cityLabel: string | null;
  /**
   * The full primary address, for the city line's tooltip only. Keeps the
   * detail one hover away without spending card width on it.
   */
  addressLabel: string | null;
  /** ISO-3166-1 alpha-2 of the address above, e.g. "IE". */
  countryCode: string | null;
  /**
   * Work sitting with this subcontractor, awaiting their answer. **Always 0** —
   * assignment doesn't exist yet, it's a later piece of work. Null on
   * resources, which aren't sent work at all.
   */
  assignedCount: number | null;
  /**
   * Work the subcontractor has confirmed back. **Always 0** — same reason as
   * {@link assignedCount}, and acceptance additionally needs the myExchange /
   * portal round-trip to report anything.
   */
  acceptedCount: number | null;
  /**
   * This subcontractor's concurrency ceiling, from
   * `Party.maxConcurrentAssignments`. Null means nobody has set one — the card
   * then shows {@link DEFAULT_SUBCONTRACTOR_MAX} without claiming it was chosen.
   * Null on resources, which have no cap.
   */
  assignmentCap: number | null;
  /**
   * Badge pinned to the card's top-right corner, with its own palette. Two
   * things claim that corner — a subcontractor's channel and the `SUB` mark on
   * a subcontractor-supplied trailer — so they share one field rather than the
   * card growing a conditional per badge type.
   */
  topBadge: { label: string; accent: string } | null;
  /**
   * The supplying haulier's party id, on cards that represent something a
   * subcontractor provides. Set on subcontractor cards (their own id) and on
   * subcontractor trailers (the party supplying the unit); null on our own fleet.
   * Assignment needs it to name who's supplying a subcontracted slot.
   */
  subcontractorPartyId: string | null;
  /**
   * True for a trailer supplied by a subcontractor rather than owned by us.
   * These aren't `Resource` rows, so `resourceType` can't distinguish them —
   * and the card leans on it to emphasise whose unit it is.
   */
  isSubcontractorUnit: boolean;
}

/**
 * How work reaches a subcontractor.
 *
 * <p>
 * `myExchange` is the network handoff: the party carries a `networkId`, so
 * they're reachable on the exchange and a job can be pushed straight through.
 * Everyone else gets emailed — that's the fallback, not a configured choice,
 * which is why it's derived rather than stored.
 * </p>
 *
 * <p>
 * `portal` is a planned third channel. The palette and label are here so the
 * badge is ready, but nothing produces it yet — unlike the other two it isn't
 * derivable from anything on `Party`, and where it gets stored is still open.
 * </p>
 */
export type SubcontractorChannel = 'myExchange' | 'email' | 'portal';

export const CHANNEL_LABEL: Record<SubcontractorChannel, string> = {
  myExchange: 'myExchange',
  email: 'Email',
  portal: 'Portal',
};

/**
 * Yellow for email, not the amber used by service tags — the two would be
 * indistinguishable sitting side by side in the same chip row.
 */
export const CHANNEL_ACCENT: Record<SubcontractorChannel, string> = {
  myExchange: 'bg-blue-100 text-blue-700',
  email: 'bg-yellow-100 text-yellow-800',
  portal: 'bg-purple-100 text-purple-700',
};

/**
 * A subcontractor's channel. A linked network id means they're on the exchange;
 * everything else falls back to email. Trimmed because an empty-string
 * `networkId` is "not linked", not "linked to nothing".
 */
export function channelFor(networkId: string | null | undefined): SubcontractorChannel {
  return networkId?.trim() ? 'myExchange' : 'email';
}

/**
 * Fallback assignment cap for a subcontractor the planner hasn't set one on.
 *
 * <p>
 * A guess, not a policy — there's nowhere to persist a real cap yet, so this
 * exists purely to give the meter a denominator. Expect every card to show it
 * until the value has somewhere to live.
 * </p>
 */
export const DEFAULT_SUBCONTRACTOR_MAX = 5;

/** How a subcontractor's cap is being consumed, ready to render. */
export interface Capacity {
  assigned: number;
  accepted: number;
  /** Cap left over. 0 once the cap is met or blown. */
  free: number;
  /** How far past the cap we are. 0 normally — a warning when it isn't. */
  over: number;
  /** Bar segment widths as percentages, already clamped to total 100. */
  assignedPct: number;
  acceptedPct: number;
}

/**
 * Split a subcontractor's cap into assigned / accepted / free.
 *
 * <p>
 * The two used counts are disjoint — an assigned load is awaiting an answer, an
 * accepted one has had it — so both consume capacity and `free` is whatever the
 * cap has left. Going over is possible and not filtered out: the cap is a
 * planner's in-memory guess while real loads come from the database, so the
 * honest thing is to show the breach rather than clamp it out of sight.
 * </p>
 */
export function capacityOf(assigned: number, accepted: number, max: number): Capacity {
  const used = assigned + accepted;
  // A zero or negative cap would divide by zero; treat the bar as full so the
  // segments still render in proportion to each other.
  const denominator = max > 0 ? Math.max(max, used) : Math.max(used, 1);
  return {
    assigned,
    accepted,
    free: Math.max(0, max - used),
    over: Math.max(0, used - Math.max(max, 0)),
    assignedPct: (assigned / denominator) * 100,
    acceptedPct: (accepted / denominator) * 100,
  };
}

/**
 * Trailers, trucks and rigids — the units that have a depot and a whereabouts.
 * Drivers carry their location in the subtitle already, and subcontractors are
 * parties with neither, so both are excluded.
 */
export const isFleet = (c: ResourceCardVM) =>
  c.resourceType === 'Trailer' || c.resourceType === 'Truck' || c.resourceType === 'Rigid';

/** Assignment readiness shown on a vehicle row: worst alert wins, else free. */
export type AssignState = 'free' | 'warn' | 'stop' | 'inUse';

export function assignState(c: ResourceCardVM): AssignState {
  if (c.alerts.some((a) => a.level === 'stop')) return 'stop';
  if (c.alerts.length > 0) return 'warn';
  return c.linkedDriverNames.length > 0 ? 'inUse' : 'free';
}

/** Pick-me-first ordering: what you can take now, down to what you can't. */
export const ASSIGN_STATE_ORDER: Record<AssignState, number> = {
  free: 0,
  inUse: 1,
  warn: 2,
  stop: 3,
};

export const ASSIGN_STATE_LABEL: Record<AssignState, string> = {
  free: 'Free',
  warn: 'Warn',
  stop: 'Stop',
  inUse: 'In use',
};

export const ASSIGN_STATE_ACCENT: Record<AssignState, string> = {
  free: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  stop: 'bg-red-100 text-red-700',
  // Amber, not grey: an in-use vehicle is still selectable, but taking it means
  // pulling it off another driver — that deserves a second look.
  inUse: 'bg-amber-100 text-amber-700',
};

/** Alert message colour, matched to its pill. */
export const ALERT_ACCENT: Record<AlertLevel, string> = {
  warn: 'text-amber-600',
  stop: 'text-red-600',
};

/**
 * Alphabetical by the name shown on the card. The resources API sorts by type
 * then `name`, which doesn't match once a resource has a different display name,
 * so pools are re-sorted here on what the planner actually reads.
 */
export const byTitle = (a: ResourceCardVM, b: ResourceCardVM) =>
  a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

/**
 * Apply the cockpit's depot filter: the selected depot's own resources first,
 * then the unassigned ones, with other depots' hidden. No depot selected →
 * everything, untouched.
 *
 * <p>
 * Partitioning only — neither group is re-sorted, so callers' {@link byTitle}
 * order survives inside each. The two groups do read as one broken A→Z run if
 * you can't tell them apart, which is why fleet cards label their depot: the
 * boundary has to be visible on the card for the grouping to make sense.
 * </p>
 */
export function forDepot(cards: ResourceCardVM[], depotSiteId: string | null): ResourceCardVM[] {
  if (!depotSiteId) return cards;
  return [
    ...cards.filter((c) => c.homeSiteId === depotSiteId),
    ...cards.filter((c) => c.homeSiteId == null),
  ];
}

/**
 * A driver card's second line: "07:00 · Dublin". A driver with no start location
 * on file reads "No location" rather than going blank, so the planner can
 * see it's missing instead of wondering whether the card just didn't load.
 */
export function driverSubtitle(
  startTime: string | null | undefined,
  startLocation: string | null | undefined,
): string {
  const where = startLocation?.trim() || 'No location';
  return startTime ? `${startTime} · ${where}` : where;
}

/** Build a card from a resource. Pure, so callers' memos can list real deps. */
export interface ResourceCardArgs {
  resource: Resource;
  equipmentNameById: Map<string, string>;
  /** Site id → name, for the depot fallback on the location line. */
  siteNameById: Map<string, string>;
  subtitle: string;
  note?: string | null;
  showTacho?: boolean;
  /** The driver's default vehicle, resolved by the caller. */
  linkedVehicle?: LinkedVehicle | null;
  /** Drivers who have this vehicle as their default — makes it "In use". */
  linkedDriverNames?: string[];
}

export function resourceCard({
  resource: r,
  equipmentNameById,
  siteNameById,
  subtitle,
  note = null,
  showTacho = false,
  linkedVehicle = null,
  linkedDriverNames = [],
}: ResourceCardArgs): ResourceCardVM {
  const equipmentName = r.organisationEquipmentTypeId
    ? (equipmentNameById.get(r.organisationEquipmentTypeId) ?? null)
    : null;
  const isDriver = r.type === 'Driver';
  const caps = r.certifications.map(capOf);

  return {
    id: r.id,
    title: r.displayName || r.name,
    subtitle,
    // Equipment type is promoted to the leading chip on fleet cards, so don't
    // repeat it on the meta line there.
    meta: isDriver ? equipmentName : null,
    resourceType: r.type,
    // "Standard Box Rigid" beats a bare "Rigid"; a Truck has no equipment type
    // so it falls back to the resource type.
    typeChip: isDriver ? null : (equipmentName ?? r.type),
    // Certifications matter for drivers (ADR, CPC…). On vehicles and trailers
    // they're just MOT/insurance paperwork noise — keep only the service tags,
    // which say what the unit can actually carry.
    caps: isDriver ? caps : caps.filter((c) => c.serviceTag),
    // Alerts come from the cert rows themselves, so they're still derived for
    // vehicles even though their cert chips are hidden.
    alerts: certAlerts(r.certifications),
    linkedDriverNames,
    available: r.status === 'Active',
    showTacho,
    linkedVehicle,
    note,
    homeSiteId: r.homeSiteId,
    depotLabel: r.homeSiteId ? (siteNameById.get(r.homeSiteId) ?? null) : null,
    // Telematics isn't wired, so nobody knows where the unit is yet.
    currentLocationLabel: null,
    odometerKm: null,
    // Subcontractor-only — a resource is dispatched, not sent work, and its
    // whereabouts is the depot line rather than a postal address.
    channel: null,
    cityLabel: null,
    addressLabel: null,
    countryCode: null,
    assignedCount: null,
    acceptedCount: null,
    assignmentCap: null,
    topBadge: null,
    subcontractorPartyId: null,
    isSubcontractorUnit: false,
  };
}
